import React from 'react';

import { Link as MuiLink, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';

import DialogGrid from 'components/_dialogs/_components/DialogGrid';
import FooterButton from 'components/FooterButton';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import statisticsApi from 'config/api/statistics/statistics';
import { omitTemplatesDashboardMainOnlyParams } from 'config/api/statistics/statistics.parsers';
import type { TemplateDashboardInstanceLink, TemplatesDashboardInstancesQueryParams } from 'config/api/statistics/statistics.types';
import { getActionLibraryInstanceDetailLinkFromDashboard } from 'helpers/actionLibraryInstanceDetailNavigation';
import type { ParamsType } from 'helpers/createUrlWithParams/createUrlWithParams';
import action_instances_dashboard_messages from 'messages/action_instances_dashboard_messages';
import general_messages from 'messages/general_messages';
import useStyles from 'pages/ActionInstancesDashboardPage/_components/TemplatesPivotInstancesDialog.styles';

const PAGE_SIZE = 25;

type Props = {
  open: boolean;
  onClose: () => void;
  templateId: number | null;
  column: 'a' | 'k' | 't';
  baseQueryParams: ParamsType;
  title: string;
};

const TemplatesPivotInstancesDialog: React.FC<Props> = ({ open, onClose, templateId, column, baseQueryParams, title }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const enabled = open && templateId != null;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = useInfiniteQuery(
    [QUERY_KEYS.GET_STATISTICS_TEMPLATES_DASHBOARD_INSTANCES, templateId, column, baseQueryParams],
    async ({ pageParam = 0 }) => {
      const params: ParamsType & TemplatesDashboardInstancesQueryParams = {
        ...omitTemplatesDashboardMainOnlyParams(baseQueryParams),
        template_id: templateId!,
        column,
        limit: PAGE_SIZE,
        offset: pageParam,
      };
      const list = await statisticsApi.getTemplatesDashboardInstances(params);
      return { results: list, nextOffset: list.length < PAGE_SIZE ? null : pageParam + PAGE_SIZE };
    },
    {
      enabled,
      getNextPageParam: lastPage => lastPage.nextOffset ?? undefined,
    },
  );

  const flat: TemplateDashboardInstanceLink[] = data?.pages.flatMap(p => p.results) ?? [];

  return (
    // @ts-ignore DialogGrid is JSX without TypeScript props
    <DialogGrid fullWidth loading={isLoading} maxWidth='md' onClose={onClose} open={open} title={title}>
      {isError && (
        <Typography color='error' variant='body2'>
          {t(general_messages.api_error)}
        </Typography>
      )}
      {!isLoading && !isError && flat.length === 0 && (
        <Typography variant='body2'>{t(action_instances_dashboard_messages.dialog_empty)}</Typography>
      )}
      {flat.length > 0 && (
        <div className={classes.gridTable}>
          <div className={classes.gridHeader}>
            <Typography className={classes.headerCaption} color='textSecondary' variant='caption'>
              {t(action_instances_dashboard_messages.dialog_column_instance)}
            </Typography>
            <Typography className={classes.headerCaptionAsset} color='textSecondary' variant='caption'>
              {t(action_instances_dashboard_messages.dialog_column_asset)}
            </Typography>
          </div>
          {flat.map(row => (
            <div key={row.id} className={classes.gridRow}>
              <div className={classes.colInstance}>
                <MuiLink
                  className={classes.instanceLink}
                  component={RouterLink}
                  to={getActionLibraryInstanceDetailLinkFromDashboard(row.id)}
                  variant='body2'
                >
                  {row.title}
                </MuiLink>
              </div>
              <div className={classes.colAsset}>
                <Typography className={classes.assetName} color='textSecondary' variant='body2'>
                  {row.assetName}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      )}
      {hasNextPage && (
        <div className={classes.loadMoreWrap}>
          <FooterButton className='' loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
            {t(action_instances_dashboard_messages.load_more_instances)}
          </FooterButton>
        </div>
      )}
    </DialogGrid>
  );
};

export default TemplatesPivotInstancesDialog;
